import { Link } from "react-router-dom";
import { SEO } from "@/lib/seo";
import { CompareBar } from "./CompareBar";

/**
 * Brand B — PulseBoard landing page built to prebuild-docs/24 (Candidate A,
 * Sea-Glass Teal classic). Uses the existing SPA tokens from index.css:
 * cooler off-white canvas, Fraunces display serif, Geist body sans, existing
 * pb-* palette.
 *
 * Design calibration (taste-skill dials):
 *   DESIGN_VARIANCE=3 — editorial, symmetric
 *   MOTION_INTENSITY=3 — breathe indicator + subtle hover only
 *   VISUAL_DENSITY=4 — feels lighter than Brand A (stark white vs warm paper)
 *
 * Content is deliberately parallel to LandingBrandA so the visual difference
 * is the only variable.
 */
export default function LandingBrandB() {
  return (
    <>
      <CompareBar />
      <SEO
        title="PulseBoard — The noticeboard Australian GPs open first"
        description="A clinician-first workplace portal — public health alerts, specialist availability, clinical tools, and referral transport on one calm surface."
        path="/compare/brand-b"
        noindex
      />

      <div className="min-h-[100dvh] bg-pb-bg font-body text-pb-ink">
        {/* ── Nav ─────────────────────────────────────────── */}
        <nav className="sticky top-[42px] z-[18] border-b border-pb-border/60 bg-pb-bg/85 backdrop-blur-md">
          <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-6 px-6">
            <div className="flex items-center gap-3">
              <span className="relative inline-flex h-2.5 w-2.5">
                <span className="absolute inset-0 animate-pb-ring-expand rounded-full bg-pb-primary/40" />
                <span className="relative h-2.5 w-2.5 animate-pb-breathe rounded-full bg-pb-primary" />
              </span>
              <span className="font-display text-xl font-semibold tracking-tight">
                PulseBoard
              </span>
              <span className="ml-1 border-l border-pb-border pl-3 text-[11px] font-medium uppercase tracking-[0.15em] text-pb-ink-3">
                Canberra · A.C.T.
              </span>
            </div>
            <ul className="hidden items-center gap-1 text-sm md:flex">
              {[
                ["Product", "#product"],
                ["Pillars", "#pillars"],
                ["A morning", "#morning"],
                ["Severity", "#severity"],
                ["Voice", "#voice"],
              ].map(([l, h]) => (
                <li key={h}>
                  <a
                    href={h}
                    className="rounded-full px-3 py-1.5 text-pb-ink-2 transition hover:bg-pb-primary/5 hover:text-pb-ink"
                  >
                    {l}
                  </a>
                </li>
              ))}
            </ul>
            <div className="flex items-center gap-2">
              <a
                href="#signup"
                className="rounded-full border border-pb-border px-4 py-1.5 text-sm font-medium text-pb-ink transition hover:border-pb-primary"
              >
                Sign in
              </a>
              <a
                href="#signup"
                className="rounded-full bg-pb-primary px-4 py-1.5 text-sm font-medium text-white transition hover:bg-pb-primary-600"
              >
                Request access
              </a>
            </div>
          </div>
        </nav>

        {/* ── Hero ────────────────────────────────────────── */}
        <header className="border-b border-pb-border/60">
          <div className="mx-auto grid max-w-6xl gap-20 px-6 py-24 md:grid-cols-[1.3fr_1fr] md:items-center">
            <div>
              <div className="mb-8 flex items-center gap-3 text-[11px] font-medium uppercase tracking-[0.2em] text-pb-ink-3">
                <span className="relative inline-flex h-2 w-2">
                  <span className="absolute inset-0 animate-pb-ring-expand rounded-full bg-pb-accent/40" />
                  <span className="relative h-2 w-2 animate-pb-breathe rounded-full bg-pb-accent" />
                </span>
                <span>Clinician-first workplace portal</span>
                <span className="text-pb-border-strong">—</span>
                <span>Canberra pilot · April 2026</span>
              </div>

              <h1 className="font-display text-5xl font-semibold leading-[1.02] tracking-[-0.03em] md:text-7xl">
                The noticeboard
                <br />
                Australian GPs
                <br />
                <span className="italic font-normal text-pb-primary-600">open first.</span>
              </h1>

              <p className="mt-8 max-w-[560px] text-lg leading-relaxed text-pb-ink-2">
                PulseBoard collapses the five-tab morning ritual — ACT Health, TGA
                recalls, RACGP guidelines, specialist availability, referral status —
                onto one calm surface so the next patient gets a clinician who is
                informed, not overloaded.
              </p>

              <div className="mt-10 flex flex-wrap gap-3">
                <a
                  href="#signup"
                  className="rounded-full bg-pb-primary px-6 py-3 text-sm font-medium text-white shadow-sm transition hover:bg-pb-primary-600 active:translate-y-[1px]"
                >
                  Request Canberra pilot access
                </a>
                <a
                  href="#morning"
                  className="rounded-full border border-pb-border-strong px-6 py-3 text-sm font-medium text-pb-ink transition hover:border-pb-primary"
                >
                  See a morning on PulseBoard
                </a>
              </div>

              <dl className="mt-14 grid max-w-[520px] grid-cols-2 gap-x-10 gap-y-4 border-t border-pb-border pt-6">
                {[
                  ["Status", "Canberra pilot — invite only"],
                  ["Region", "GCP australia-southeast1"],
                  ["Data residency", "Australia, always"],
                  ["Compliance", "AHPRA · RACGP · Privacy Act"],
                ].map(([k, v]) => (
                  <div key={k}>
                    <dt className="text-[10px] font-medium uppercase tracking-[0.2em] text-pb-ink-3">
                      {k}
                    </dt>
                    <dd className="mt-1 text-sm font-medium text-pb-ink">{v}</dd>
                  </div>
                ))}
              </dl>
            </div>

            {/* Hero mark */}
            <div className="grid place-items-center">
              <div className="relative rounded-3xl border border-pb-border/60 bg-white p-10 shadow-[0_20px_40px_-15px_rgba(17,34,34,0.08)]">
                <svg
                  viewBox="0 0 320 320"
                  className="w-full max-w-[420px]"
                  aria-hidden
                >
                  <defs>
                    <linearGradient id="heroWashB" x1="0" y1="0" x2="1" y2="1">
                      <stop offset="0" stopColor="rgb(var(--pb-primary))" />
                      <stop offset="1" stopColor="rgb(var(--pb-primary-600))" />
                    </linearGradient>
                  </defs>
                  <rect x="30" y="30" width="260" height="260" rx="48" fill="url(#heroWashB)" />
                  <path
                    d="M70 170 L115 170 L132 128 L155 212 L172 170 L250 170"
                    stroke="white"
                    strokeWidth="12"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <circle cx="250" cy="170" r="6" fill="rgb(var(--pb-accent))" />
                </svg>
                <div className="absolute inset-x-4 bottom-4 flex justify-between font-mono text-[10px] uppercase tracking-[0.15em] text-pb-ink-3">
                  <span>The Mark</span>
                  <span>Noticeboard + one tick</span>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* ── Positioning ─────────────────────────────────── */}
        <section id="product" className="mx-auto max-w-6xl px-6 py-20">
          <div className="rounded-3xl border border-pb-border/60 bg-pb-surface p-10 md:p-14">
            <div className="mb-4 text-[11px] font-medium uppercase tracking-[0.2em] text-pb-ink-3">
              Positioning statement
            </div>
            <blockquote className="m-0 font-display text-[clamp(24px,3vw,34px)] font-light leading-[1.3] tracking-[-0.015em] text-pb-ink">
              For Australian GPs,{" "}
              <span className="bg-gradient-to-b from-transparent from-[60%] to-pb-primary/20 to-[60%] px-0.5">
                PulseBoard
              </span>{" "}
              is the{" "}
              <span className="bg-gradient-to-b from-transparent from-[60%] to-pb-primary/20 to-[60%] px-0.5">
                first URL of the day
              </span>{" "}
              — a trusted noticeboard that gathers public-health alerts, specialist
              availability, and daily clinical tools onto one calm surface, so the next
              patient gets a clinician who is informed, not overloaded.
            </blockquote>
          </div>
        </section>

        {/* ── Six Pillars (flat 3×2 grid — classic editorial) ── */}
        <section id="pillars" className="mx-auto max-w-6xl px-6 pb-24">
          <div className="mb-10 grid gap-6 md:grid-cols-[auto_1fr] md:gap-12">
            <span className="pt-2 text-[11px] font-medium uppercase tracking-[0.2em] text-pb-primary">
              01 · Six pillars
            </span>
            <div>
              <h2 className="font-display text-4xl font-semibold leading-tight tracking-tight md:text-5xl">
                What sits on the board.
              </h2>
              <p className="mt-3 max-w-[660px] text-lg text-pb-ink-2">
                One surface, six daily functions. Clinical content stays free and unbiased;
                specialists and vendors pay for placement, on a visibly separate background.
              </p>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            {[
              { n: "i", t: "Public health feed", b: "ACT Health, TGA, RACGP, AIHW, BOM ACT, ACT ESA — curated by moderators into a single feed with 7 categories × 5 severities." },
              { n: "ii", t: "Clinical toolbox", b: "12 RACGP-aligned tools — AUSDRISK, FRAX, cancer screening — at the top of the day." },
              { n: "iii", t: "Live directory", b: "Google Calendar Watch auto-populates availability windows. No specialist login required." },
              { n: "iv", t: "Referral transport", b: "Vertex Gemini drafts. GPs review. KMS-encrypted payload, metadata-only by default." },
              { n: "v", t: "Doctor-only forum", b: "Identity-checked access, strict moderation, medicine outside marketing reach." },
              { n: "vi", t: "Marketplace", b: "Spotlight placements (AHPRA-compliant) and non-medical vendor ads on Parchment, visibly separate from editorial." },
            ].map((p) => (
              <article
                key={p.t}
                className="group rounded-2xl border border-pb-border/60 bg-white p-7 transition hover:-translate-y-[2px] hover:border-pb-primary/30 hover:shadow-[0_10px_30px_-15px_rgba(17,34,34,0.12)]"
              >
                <div className="font-mono text-[11px] uppercase tracking-[0.15em] text-pb-ink-3">
                  {p.n} · {p.t}
                </div>
                <h3 className="mt-3 font-display text-2xl font-medium leading-tight tracking-tight text-pb-ink">
                  {p.t}
                </h3>
                <p className="mt-3 text-[15px] leading-relaxed text-pb-ink-2">{p.b}</p>
              </article>
            ))}
          </div>
        </section>

        {/* ── Morning Brief Mock ──────────────────────────── */}
        <section id="morning" className="border-y border-pb-border/60 bg-pb-surface-2/60">
          <div className="mx-auto max-w-6xl px-6 py-24">
            <div className="mb-10 grid gap-6 md:grid-cols-[auto_1fr] md:gap-12">
              <span className="pt-2 text-[11px] font-medium uppercase tracking-[0.2em] text-pb-primary">
                02 · A Saturday at 07:00
              </span>
              <div>
                <h2 className="font-display text-4xl font-semibold leading-tight tracking-tight md:text-5xl">
                  What the GP actually sees.
                </h2>
                <p className="mt-3 max-w-[660px] text-lg text-pb-ink-2">
                  Good morning, 4 new alerts overnight, 1 marked urgent. No emojis. No
                  exclamation marks. Names, sources, severities, done.
                </p>
              </div>
            </div>

            <div className="overflow-hidden rounded-3xl border border-pb-border-strong bg-white shadow-[0_20px_40px_-15px_rgba(17,34,34,0.1)]">
              <div className="flex items-center gap-3 border-b border-pb-border bg-pb-surface-2 px-4 py-2.5">
                <div className="flex gap-1.5">
                  <span className="h-2.5 w-2.5 rounded-full bg-pb-border-strong" />
                  <span className="h-2.5 w-2.5 rounded-full bg-pb-border-strong" />
                  <span className="h-2.5 w-2.5 rounded-full bg-pb-border-strong" />
                </div>
                <div className="flex-1 rounded bg-white px-3 py-1 font-mono text-[11px] text-pb-ink-2">
                  pulseboard.clinicos.com.au/canberra
                </div>
              </div>

              <div className="p-8">
                {/* inner nav */}
                <div className="flex items-center gap-4 border-b border-pb-border pb-4">
                  <span className="relative inline-flex h-2 w-2">
                    <span className="relative h-2 w-2 rounded-full bg-pb-primary" />
                  </span>
                  <span className="font-semibold">PulseBoard</span>
                  <span className="border-l border-pb-border pl-3 font-mono text-[11px] text-pb-ink-3">
                    Canberra
                  </span>
                  <nav className="ml-6 flex gap-5 text-sm text-pb-ink-2">
                    <span className="font-medium text-pb-ink">Feed</span>
                    <span>Toolbox</span>
                    <span>Directory</span>
                    <span>Referrals</span>
                    <span>Forum</span>
                  </nav>
                  <span className="ml-auto text-sm text-pb-ink-2">Dr K. Reyes</span>
                </div>

                {/* Greeting */}
                <div className="border-b border-pb-border py-5">
                  <div className="font-mono text-[11px] uppercase tracking-[0.15em] text-pb-ink-3">
                    Saturday · 18 April 2026 · 06:58
                  </div>
                  <h3 className="mt-2 font-display text-3xl font-semibold tracking-tight">
                    Good morning, Dr Reyes.
                  </h3>
                  <p className="mt-2 max-w-[640px] text-pb-ink-2">
                    <strong>4 new alerts</strong> overnight — 1 urgent.{" "}
                    <strong>3 referrals</strong> awaiting specialist response. Your first
                    appointment is at 08:30.
                  </p>
                </div>

                <div className="mt-4 flex flex-col gap-2.5">
                  {[
                    {
                      sev: "L4",
                      sevClass: "bg-pb-accent/15 text-pb-accent border-pb-accent/30",
                      stripe: "bg-pb-accent",
                      id: "TGA/RECALL/2026-117",
                      meta: "06:42 · ACT",
                      title: "Voluntary recall — metformin 500mg, batch 2025M7",
                    },
                    {
                      sev: "L3",
                      sevClass: "bg-amber-100 text-amber-900 border-amber-200",
                      stripe: "bg-amber-600",
                      id: "ACTESA/2026-0418-02",
                      meta: "05:15 · Tuggeranong",
                      title: "Smoke haze — controlled burn south of Tuggeranong",
                    },
                    {
                      sev: "L2",
                      sevClass: "bg-pb-primary/10 text-pb-primary border-pb-primary/20",
                      stripe: "bg-pb-primary/40",
                      id: "RACGP/2026-Q2-04",
                      meta: "Yesterday · National",
                      title: "Updated cervical screening — self-collect pathway",
                    },
                  ].map((a) => (
                    <article
                      key={a.id}
                      className="grid overflow-hidden rounded-xl border border-pb-border bg-white"
                      style={{ gridTemplateColumns: "4px 1fr" }}
                    >
                      <div className={a.stripe} />
                      <div className="px-4 py-3">
                        <div className="flex flex-wrap items-center gap-2 text-[11px]">
                          <span
                            className={`rounded border px-2 py-0.5 font-mono uppercase tracking-[0.1em] ${a.sevClass}`}
                          >
                            {a.sev}
                          </span>
                          <span className="font-mono text-pb-ink-2">{a.id}</span>
                          <span className="font-mono text-pb-ink-3">{a.meta}</span>
                        </div>
                        <h4 className="mt-1.5 text-[15px] font-medium text-pb-ink">
                          {a.title}
                        </h4>
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Severity (3-tier simpler treatment) ──────────── */}
        <section id="severity" className="mx-auto max-w-6xl px-6 py-24">
          <div className="mb-10 grid gap-6 md:grid-cols-[auto_1fr] md:gap-12">
            <span className="pt-2 text-[11px] font-medium uppercase tracking-[0.2em] text-pb-primary">
              03 · Severity
            </span>
            <div>
              <h2 className="font-display text-4xl font-semibold leading-tight tracking-tight md:text-5xl">
                A gradient of concern.
              </h2>
              <p className="mt-3 max-w-[660px] text-lg text-pb-ink-2">
                We reject the traffic-light cliché. Severity escalates through chroma and
                hue warmth. Coral is reserved for urgency. Red, rarer still.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-px overflow-hidden rounded-2xl border border-pb-border/60 md:grid-cols-5">
            {[
              { l: "L1 · Info", bg: "bg-pb-surface-2", fg: "text-pb-ink", d: "Context, background, non-urgent updates." },
              { l: "L2 · Advisory", bg: "bg-amber-50", fg: "text-amber-900", d: "New guidance, non-critical changes, FYI." },
              { l: "L3 · Caution", bg: "bg-orange-100", fg: "text-orange-900", d: "Act soon. Review before next shift." },
              { l: "L4 · Urgent", bg: "bg-pb-accent/30", fg: "text-pb-accent", d: "Same-day action. Recalls, outbreak alerts." },
              { l: "L5 · Critical", bg: "bg-red-900", fg: "text-white", d: "Immediate. Emergency response required." },
            ].map((s) => (
              <div key={s.l} className={`flex min-h-[140px] flex-col gap-2 p-5 ${s.bg} ${s.fg}`}>
                <div className="font-mono text-[11px] uppercase tracking-[0.15em] opacity-80">
                  {s.l}
                </div>
                <div className="font-display text-xl font-medium">{s.l.split(" · ")[1]}</div>
                <div className="text-sm leading-snug opacity-80">{s.d}</div>
              </div>
            ))}
          </div>
        </section>

        {/* ── Voice examples ──────────────────────────────── */}
        <section id="voice" className="border-t border-pb-border/60 bg-pb-surface">
          <div className="mx-auto max-w-4xl px-6 py-24">
            <div className="mb-10 grid gap-6 md:grid-cols-[auto_1fr] md:gap-12">
              <span className="pt-2 text-[11px] font-medium uppercase tracking-[0.2em] text-pb-primary">
                04 · Voice
              </span>
              <div>
                <h2 className="font-display text-4xl font-semibold leading-tight tracking-tight md:text-5xl">
                  On a Monday before coffee.
                </h2>
                <p className="mt-3 max-w-[660px] text-lg text-pb-ink-2">
                  Specific. Plain-spoken. Deferential. Never breathless.
                </p>
              </div>
            </div>

            <div className="divide-y divide-pb-border">
              {[
                {
                  no: "🎉 Exciting news! We've just launched AI-powered referrals that will revolutionise your workflow!",
                  yes: "Vertex Gemini can draft a referral from your notes. The GP reviews before send; every draft carries an AI-assisted banner. Opt in per referral.",
                },
                {
                  no: "Oops! Something went wrong. Please try again later 😬",
                  yes: "The ACT Health feed is offline. We last refreshed at 06:42. Your alert queue is unchanged; you'll see the next poll at 07:00.",
                },
                {
                  no: "Welcome to PulseBoard — your all-in-one solution for modern general practice!",
                  yes: "Good morning, Dr Reyes. 4 new alerts overnight — 1 TGA recall marked urgent. Waiting list last updated 05:15.",
                },
              ].map((ex, i) => (
                <div key={i} className="grid grid-cols-[120px_1fr] gap-6 py-6">
                  <div>
                    <div className="mb-2 font-mono text-[10px] uppercase tracking-[0.2em] text-pb-danger">
                      Not this
                    </div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-pb-primary">
                      This
                    </div>
                  </div>
                  <div className="grid gap-3">
                    <p className="m-0 rounded-lg border border-pb-accent/20 bg-pb-accent/5 p-3.5 text-pb-ink-2 line-through decoration-pb-danger/40">
                      {ex.no}
                    </p>
                    <p className="m-0 rounded-lg border border-pb-primary/20 bg-pb-primary/5 p-3.5 text-pb-ink">
                      {ex.yes}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Signup ──────────────────────────────────────── */}
        <section id="signup" className="px-6 py-24">
          <div className="mx-auto max-w-[760px] rounded-3xl bg-pb-ink px-10 py-14 text-center text-white">
            <div className="mb-4 font-mono text-[11px] uppercase tracking-[0.2em] text-white/60">
              Canberra pilot — invite only
            </div>
            <h2 className="font-display text-4xl font-semibold leading-tight tracking-tight md:text-5xl">
              For the GP who opens
              <br />
              five tabs before coffee.
            </h2>
            <p className="mx-auto mt-5 max-w-[540px] text-white/75">
              Request access for your practice. We'll reach out with onboarding details
              and the next pilot window.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <a
                href="mailto:hello@pulseboard.clinicos.com.au?subject=Canberra%20pilot%20access"
                className="rounded-full bg-pb-accent px-6 py-3 text-sm font-medium text-pb-ink transition hover:bg-pb-accent/90 active:translate-y-[1px]"
              >
                Request pilot access
              </a>
              <Link
                to="/compare/brand-a"
                className="rounded-full border border-white/20 px-6 py-3 text-sm font-medium text-white/70 transition hover:border-white/40 hover:text-white"
              >
                See Brand A
              </Link>
            </div>
          </div>
        </section>

        {/* ── Footer ──────────────────────────────────────── */}
        <footer className="flex flex-wrap items-center justify-between gap-3 border-t border-pb-border/60 px-6 py-10 font-mono text-xs text-pb-ink-3">
          <div>© 2026 ClinicOS Pty Ltd · Canberra pilot</div>
          <div>PulseBoard · Clinical referral availability platform</div>
        </footer>
      </div>
    </>
  );
}
